import { TableContainer } from 'components/ideal-core-ui'
import { AgGridReact } from 'ag-grid-react'
import { TABLE_ROW_SERVER_SIDE_MODEL } from 'utils/constants'
import { useOutletContext } from 'react-router-dom'
import { currencyFormatter, percentageFormatter, timezoneDate } from 'utils/helpers'
import { NameCellRenderer } from './nameColumn/NameCellRenderer'
import {
  CompaniesCellRenderer,
  AwardedToCellRenderer
} from './companiesColumn/CompaniesCellRenderer'
import { fromConstantPropertyToSelectValue } from 'utils/serializers'

const columnDefs = [
  {
    headerName: 'Follow-up Date',
    field: 'opportunity_followup_date',
    valueGetter: params => {
      if (!params || !params.data) return ''
      return params.data.opportunity_followup_date
        ? timezoneDate(params.data.opportunity_followup_date)
        : ''
    },
    valueFormatter: params => {
      if (!params || !params.data) return ''
      return params.data.opportunity_followup_date
        ? timezoneDate(params.data.opportunity_followup_date)
        : ''
    }
  },
  {
    headerName: 'Bid Due Date',
    field: 'opportunity_due_date',
    valueGetter: params => {
      if (!params || !params.data) return ''
      return params.data.opportunity_due_date ? timezoneDate(params.data.opportunity_due_date) : ''
    },
    valueFormatter: params => {
      if (!params || !params.data) return ''
      return params.data.opportunity_due_date ? timezoneDate(params.data.opportunity_due_date) : ''
    }
  },
  {
    headerName: 'Opportunity Manager',
    field: 'op_manager_full_name'
  },
  {
    headerName: 'Opportunity Name',
    field: 'opportunity_name',
    cellClass: 'deals-name-column',
    cellRenderer: 'agGroupCellRenderer',
    cellRendererParams: {
      path: 'opportunities',
      innerRendererFramework: NameCellRenderer
    }
  },
  {
    headerName: 'City',
    field: 'opportunity_city'
  },
  {
    headerName: 'State/Prov/Reg',
    field: 'opportunity_state'
  },
  {
    headerName: 'Status',
    field: 'opportunity_status'
  },
  {
    headerName: 'Awarded',
    field: 'awarded'
  },
  {
    headerName: 'Awarded To',
    valueGetter: params => {
      if (!params.data || !params.data.awarded_company_name) return ''
      return params.data.awarded_company_name
    },
    field: 'awarded_company_name',
    cellRendererFramework: AwardedToCellRenderer
  },
  {
    headerName: 'Client',
    valueGetter: params => {
      if (!params.data || !params.data.client_name) return ''
      return params.data.client_name
    },
    field: 'client_name',
    cellRendererFramework: CompaniesCellRenderer
  },
  {
    headerName: 'Proposal Status',
    valueGetter: params => {
      if (!params || !params.data) return ''
      return params.data.last_proposal_status
        ? fromConstantPropertyToSelectValue(params.data.last_proposal_status, 'ProposalStatus')
            ?.label
        : ''
    },
    field: 'last_proposal_status',
    valueFormatter: params => {
      return params && params.value
        ? fromConstantPropertyToSelectValue(params.value, 'ProposalStatus')?.label
        : ''
    }
  },
  {
    headerName: 'Sent To',
    field: 'last_sent_email_address'
  },
  {
    headerName: 'Value',
    field: 'opportunity_value',
    cellClass: 'center-align justify-content',
    valueFormatter: currencyFormatter
  },
  {
    headerName: 'Cost',
    field: 'opportunity_cost',
    cellClass: 'center-align justify-content',
    valueFormatter: currencyFormatter
  },
  {
    headerName: 'Gross Profit',
    field: 'opportunity_gross_profit',
    cellClass: 'center-align justify-content',
    valueFormatter: currencyFormatter
  },
  {
    headerName: 'Margin',
    field: 'opportunity_margin',
    cellClass: 'center-align justify-content',
    valueFormatter: percentageFormatter
  },
  {
    headerName: 'Markup',
    field: 'opportunity_markup',
    cellClass: 'center-align justify-content',
    valueFormatter: percentageFormatter
  },
  {
    headerName: 'Forecast Revenue',
    field: 'opportunity_forecast_revenue',
    cellClass: 'center-align justify-content',
    valueFormatter: currencyFormatter
  },
  {
    headerName: 'Forecast Profit',
    field: 'opportunity_forecast_profit',
    cellClass: 'center-align justify-content',
    valueFormatter: currencyFormatter
  },
  {
    headerName: 'Expected Close Date',
    field: 'opportunity_expected_close_date',
    valueGetter: params => {
      if (!params || !params.data) return ''
      return params.data.opportunity_expected_close_date
        ? timezoneDate(params.data.opportunity_expected_close_date)
        : ''
    },
    valueFormatter: params => {
      if (!params || !params.data) return ''
      return params.data.opportunity_expected_close_date
        ? timezoneDate(params.data.opportunity_expected_close_date)
        : ''
    }
  },
  {
    headerName: 'Project Start Date',
    field: 'opportunity_project_start_date',
    valueGetter: params => {
      if (!params || !params.data) return ''
      return params.data.opportunity_project_start_date
        ? timezoneDate(params.data.opportunity_project_start_date)
        : ''
    },
    valueFormatter: params => {
      if (!params || !params.data) return ''
      return params.data.opportunity_project_start_date
        ? timezoneDate(params.data.opportunity_project_start_date)
        : ''
    }
  },
  {
    headerName: 'Project End Date',
    field: 'opportunity_project_end_date',
    valueGetter: params => {
      if (!params || !params.data) return ''
      return params.data.opportunity_project_end_date
        ? timezoneDate(params.data.opportunity_project_end_date)
        : ''
    },
    valueFormatter: params => {
      if (!params || !params.data) return ''
      return params.data.opportunity_project_end_date
        ? timezoneDate(params.data.opportunity_project_end_date)
        : ''
    }
  },
  {
    headerName: 'Project Duration',
    field: 'opportunity_project_duration'
  },
  {
    headerName: 'Won Date',
    field: 'opportunity_won_date',
    valueGetter: params => {
      if (!params || !params.data) return ''
      return params.data.opportunity_won_date ? timezoneDate(params.data.opportunity_won_date) : ''
    },
    valueFormatter: params => {
      if (!params || !params.data) return ''
      return params.data.opportunity_won_date ? timezoneDate(params.data.opportunity_won_date) : ''
    }
  },
  {
    headerName: 'Lost Date',
    field: 'opportunity_lost_date',
    valueGetter: params => {
      if (!params || !params.data) return ''
      return params.data.opportunity_lost_date
        ? timezoneDate(params.data.opportunity_lost_date)
        : ''
    },
    valueFormatter: params => {
      if (!params || !params.data) return ''
      return params.data.opportunity_lost_date
        ? timezoneDate(params.data.opportunity_lost_date)
        : ''
    }
  },
  {
    headerName: 'Category',
    field: 'opportunity_category_name'
  },
  {
    headerName: 'Health',
    valueGetter: params => {
      if (!params || !params.data) return ''
      return params.data.opportunity_health
        ? fromConstantPropertyToSelectValue(params.data.opportunity_health, 'DealHealth')?.label
        : ''
    },
    field: 'health',
    valueFormatter: params => {
      return params && params.value
        ? fromConstantPropertyToSelectValue(params.value, 'DealHealth')?.label
        : ''
    }
  },
  {
    headerName: 'Size',
    field: 'opportunity_size',
    valueGetter: params => {
      if (!params || !params.data) return ''
      return params.data.opportunity_size
        ? fromConstantPropertyToSelectValue(params.data.opportunity_size, 'DealSize')?.label
        : ''
    },
    valueFormatter: params => {
      return params && params.value
        ? fromConstantPropertyToSelectValue(params.value, 'DealSize')?.label
        : ''
    }
  },
  {
    headerName: 'Quote Number',
    field: 'quote_number'
  },
  {
    headerName: 'Project Number',
    field: 'project_number'
  }
]

const DealsList = () => {
  const { filteredByUser } = useOutletContext()
  return (
    <TableContainer
      filteredByUserId={filteredByUser.value}
      canSaveAsList
      tableKey="deal"
      rowModelType={TABLE_ROW_SERVER_SIDE_MODEL}
      fetchUrl="/v1/aggrid/fetchDealRows"
      selectable
    >
      {gridData => <AgGridReact {...gridData} columnDefs={columnDefs} />}
    </TableContainer>
  )
}

export default DealsList
